import type { Record } from "@suse-o11y/record";
import React, { useCallback, useState } from "react";

import type { BorgAgentType, BorgMessage } from "../models/borg-types";

import { AgentTypeBadge } from "./_agent-type-badge";
import { CollapsiblePinList } from "./_collapsible-pin-list";
import { ContextChip as ContextChipBadge } from "./_context-chip";
import { areEquivalentParts, isMessageActivelyThinking, withStableGroupKeys } from "./_message-thread-utils";
import { MessageTimestamp } from "./_message-timestamp";
import { groupAdjacentParts } from "./_message-utils";
import { PartGroupRenderer } from "./_part-group-renderer";
import type { ComponentViewMode } from "./_use-navigate-to-component";
import { ChatBubble } from "./chat-bubble";

// -- Agent type labels --

const AGENT_TYPE_LABELS = {
	investigation: "Investigation",
	promql_assist: "PromQL Assist",
	docs_assist: "Documentation",
} satisfies Record<BorgAgentType, string, "readonly", "optional">;

const hasAgentTypeLabel = (agentType: BorgAgentType): agentType is keyof typeof AGENT_TYPE_LABELS =>
	agentType in AGENT_TYPE_LABELS;

// -- MessageThread --

export interface MessageThreadProps {
	messages: ReadonlyArray<BorgMessage>;
	currentMessage?: BorgMessage;
	optimisticUserMessage?: string;
	onSuggestionClick?: (text: string) => void;
	onResend?: (text: string) => void;
	onNavigateToComponent?: (identifier: string, mode?: ComponentViewMode) => void;
}

export const MessageThread: React.FC<MessageThreadProps> = ({
	messages,
	currentMessage,
	optimisticUserMessage,
	onSuggestionClick,
	onResend,
	onNavigateToComponent,
}) => {
	const [thinkingOpenByKey, setThinkingOpenByKey] = useState<{ [key: string]: boolean }>({});
	const setThinkingOpen = useCallback((key: string, open: boolean) => {
		setThinkingOpenByKey((prev) => {
			if (prev[key] === open) {
				return prev;
			}
			return { ...prev, [key]: open };
		});
	}, []);

	// Suppress streaming message if the saved messages already contain the same content
	// (race condition: message saved to DB before conversation status goes terminal).
	const lastSaved = messages.at(-1);
	const canCompareByParts =
		currentMessage != null && (lastSaved?.id === currentMessage.id || lastSaved?.id === "" || currentMessage.id === "");
	const isDuplicate =
		currentMessage != null &&
		lastSaved?.role === "assistant" &&
		canCompareByParts &&
		areEquivalentParts(lastSaved.parts, currentMessage.parts);
	const effectiveCurrentMessage = isDuplicate ? undefined : currentMessage;

	return (
		<div className="flex flex-col gap-4">
			{/* Saved messages */}
			{messages.map((msg) => (
				<MessageGroup
					key={msg.id}
					message={msg}
					messageTogglePrefix={`saved:${msg.id}`}
					onNavigateToComponent={onNavigateToComponent}
					onResend={onResend}
					onSuggestionClick={onSuggestionClick}
					onThinkingOpenChange={setThinkingOpen}
					thinkingOpenByKey={thinkingOpenByKey}
				/>
			))}

			{/* Optimistic user message */}
			{optimisticUserMessage != null && <ChatBubble variant="user">{optimisticUserMessage}</ChatBubble>}

			{/* Current in-progress message */}
			{effectiveCurrentMessage != null && effectiveCurrentMessage.parts.length > 0 && (
				<MessageGroup
					isLive
					message={effectiveCurrentMessage}
					messageTogglePrefix="live:current"
					onNavigateToComponent={onNavigateToComponent}
					onResend={onResend}
					onSuggestionClick={onSuggestionClick}
					onThinkingOpenChange={setThinkingOpen}
					thinkingOpenByKey={thinkingOpenByKey}
				/>
			)}

			{/* Current in-progress message with empty parts — agent registered but no content yet */}
			{effectiveCurrentMessage != null &&
				effectiveCurrentMessage.parts.length === 0 &&
				effectiveCurrentMessage.agent_type != null &&
				hasAgentTypeLabel(effectiveCurrentMessage.agent_type) && (
					<AgentTypeBadge
						agentType={effectiveCurrentMessage.agent_type}
						label={AGENT_TYPE_LABELS[effectiveCurrentMessage.agent_type]}
					/>
				)}
		</div>
	);
};

// -- MessageGroup --

const MessageGroup: React.FC<{
	message: BorgMessage;
	messageTogglePrefix: string;
	isLive?: boolean;
	thinkingOpenByKey: { [key: string]: boolean };
	onThinkingOpenChange: (key: string, open: boolean) => void;
	onSuggestionClick?: (text: string) => void;
	onResend?: (text: string) => void;
	onNavigateToComponent?: (identifier: string, mode?: ComponentViewMode) => void;
}> = ({
	message,
	messageTogglePrefix,
	isLive,
	thinkingOpenByKey,
	onThinkingOpenChange,
	onSuggestionClick,
	onResend,
	onNavigateToComponent,
}) => {
	if (message.role === "user") {
		const text = message.parts.find((p) => p.type === "text")?.content ?? "";
		return (
			<div>
				<ChatBubble onResend={onResend ? () => onResend(text) : undefined} variant="user">
					{text}
				</ChatBubble>
				{message.context_pins != null && message.context_pins.length > 0 && (
					<CollapsiblePinList
						className="mt-1"
						pins={message.context_pins}
						renderPin={(pin) => <ContextChipBadge pin={pin} />}
					/>
				)}
				<MessageTimestamp className="text-right" createdAt={message.created_at} />
			</div>
		);
	}

	// Assistant message
	const agentType = message.agent_type;
	const baseLabel = agentType != null && hasAgentTypeLabel(agentType) ? AGENT_TYPE_LABELS[agentType] : undefined;
	const selectionSuffix = message.selection_mode === "auto" ? " (auto-routed)" : "";
	const agentLabel = baseLabel ? `${baseLabel}${selectionSuffix}` : undefined;
	const isLiveThinking = isLive === true && isMessageActivelyThinking(message.parts);
	const groups = groupAdjacentParts(message.parts);
	const groupsWithKeys = withStableGroupKeys(groups);
	const latestThinkingGroupKey = [...groupsWithKeys].reverse().find(({ group }) => group.key === "thinking")?.key;

	return (
		<div className="flex flex-col gap-2">
			{agentLabel && agentType != null && <AgentTypeBadge agentType={agentType} label={agentLabel} />}
			{groupsWithKeys.map(({ group, key }) => (
				<PartGroupRenderer
					group={group}
					isLiveThinking={isLiveThinking && group.key === "thinking" && key === latestThinkingGroupKey}
					key={key}
					onNavigateToComponent={onNavigateToComponent}
					onSuggestionClick={onSuggestionClick}
					onThinkingOpenChange={onThinkingOpenChange}
					thinkingOpenByKey={thinkingOpenByKey}
					thinkingToggleKey={`${messageTogglePrefix}:${key}`}
				/>
			))}
			<MessageTimestamp createdAt={message.created_at} />
		</div>
	);
};
